import React, {useState, useEffect} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {useParams, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import "pages/roles/_roleDetail.scss";
import {GROUND_ADMIN_PERMISSIONS, ADMIN_PERMISSIONS, ROLES, ROLE_DISPLAY, ADMIN_ROLE_DISPLAY} from 'common/constants';
import {api} from 'api/calls';
import ComLoader from 'components/comLoader/ComLoader';
import {routes} from 'common';
import {getAvailablePermissions, getPermissionDisplayName, subRoleValidationSchema} from 'pages/roles/constants';
import {Loader, ConfirmModal} from 'components';

function RoleDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [roleData, setRoleData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchRoleData = async () => {
      try {
        setFetchLoading(true);
        const response = await api.getSubRoleDetail(id);
        setRoleData(response);
      } catch (error) {
        toast.error('Failed to fetch role data');
        navigate('/roles');
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchRoleData();
    }
  }, [id, navigate]);

  const initialValues = {
    name: roleData?.name || '',
    description: roleData?.description || '',
    role: roleData?.role || ROLES.GROUND_ADMIN,
    permissions: roleData?.permissions || [],
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await api.updateSubRole(id, values);
      navigate(routes.ROLES);
      toast.success('Role updated successfully!');
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.deleteSubRole(id);
      toast.success('Role deleted successfully!');
      navigate(routes.ROLES);
    } catch (error) {
      console.error('Error deleting role:', error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => {
    navigate(routes.ROLES);
  };

  if (!roleData && !fetchLoading) {
    return (
      <div className="role-detail-page detail-page">
        <div className="error-container">
          <p>Role not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-detail-page detail-page">
      {fetchLoading && (<Loader/>)}
      <div className="header">
        <h1>Edit Sub Role</h1>
        <p className="subtitle">Update sub role details and permissions</p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={subRoleValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({values, errors, touched, setFieldValue}) => {
          const availablePermissions = getAvailablePermissions(values.role);

          return (
            <Form className="custom-form">
              <div className="form-group">
                <label htmlFor="name">
                  Name <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                  placeholder="Enter name"
                />
                <ErrorMessage name="name" component="div" className="error-message"/>
              </div>

              <div className="form-group">
                <label htmlFor="description">
                  Description
                </label>
                <Field
                  as="textarea"
                  id="description"
                  name="description"
                  className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
                  placeholder="Enter description"
                  rows="4"
                />
                <ErrorMessage name="description" component="div" className="error-message"/>
              </div>

              <div className="form-group">
                <label htmlFor="role">
                  Role<span className="required">*</span>
                </label>
                <Field
                  as="select"
                  id="role"
                  name="role"
                  className={`form-control ${touched.role && errors.role ? 'is-invalid' : ''}`}
                  onChange={(e) => {
                    setFieldValue('role', e.target.value);
                    setFieldValue('permissions', []); // Reset permissions when role changes
                  }}
                >
                  {Object.entries(ADMIN_ROLE_DISPLAY).map(([key, display]) => (
                    <option key={key} value={key}>
                      {display}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="role" component="div" className="error-message"/>
              </div>

              <div className="form-group">
                <label>
                  Permissions <span className="required">*</span>
                </label>
                <div className="checkbox-container">
                  {availablePermissions.map((permission) => (
                    <div key={permission} className="checkbox-item-wrapper">
                      <label className="checkbox-item">
                        <Field
                          type="checkbox"
                          name="permissions"
                          value={permission}
                          className="checkbox-input"
                        />
                        <span className="checkbox-label">
                          {getPermissionDisplayName(permission)}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                <ErrorMessage name="permissions" component="div" className="error-message"/>
              </div>

              {roleData && (
                <div className="info-section">
                  <div className="info-item">
                    <span className="info-label">Created:</span>
                    <span className="info-value">
                      {new Date(roleData.created).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Modified:</span>
                    <span className="info-value">
                      {new Date(roleData.modified).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="actions-section">
                <button
                  className="submit-btn primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <ComLoader/> : "Save"}
                </button>

                <button
                  className="submit-btn secondary"
                  type="button"
                  onClick={handleCancel}
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>

                <button
                  className="submit-btn danger"
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loading || deleteLoading}
                >
                  Delete
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ConfirmModal
        show={showDeleteModal}
        title="Delete Role"
        message="Are you sure you want to delete this role? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default RoleDetail;